import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import MarkdownWrapper from "@/components/markdownWrapper";
import MediaWrapper from "@/components/mediaWrapper";

import { CameraIcon } from "@heroicons/react/24/solid";

export const query = graphql`
  fragment ContentTwoColumnsWithImageData on StrapiComponentContentTwoColumnsWithImage {
    title
    topic
    description
    image {
      caption
      ...StrapiMediaSubquery
    }
    body {
      ...StrapiSharedBody
    }
  }
`;

function TwoColumnsWithImage({ data }) {
  if (!data) {
    return <template />;
  }
  return (
    <div className="overflow-hidden bg-background-white">
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="absolute bottom-0 left-3/4 top-0 hidden w-screen bg-background-off-white lg:block" />
        <div className="mx-auto max-w-prose text-base lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-8">
          <div>
            {data.topic && (
              <h4 className="text-base font-semibold uppercase tracking-wide text-text-secondary">
                <MarkdownWrapper text={data.topic} />
              </h4>
            )}
            {data.title && (
              <h3 className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-text-primary sm:text-4xl">
                <MarkdownWrapper text={data.title} />
              </h3>
            )}
          </div>
        </div>
        <div className="mt-8 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="relative lg:col-start-2 lg:row-start-1">
            <svg
              className="absolute right-0 top-0 -mr-20 -mt-20 hidden lg:block"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384"
              aria-hidden="true"
            >
              <defs>
                <pattern
                  id="de316486-4a29-4312-bdfc-fbce2132a2c1"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-text-light-gray"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#de316486-4a29-4312-bdfc-fbce2132a2c1)"
              />
            </svg>
            <div className="relative mx-auto max-w-prose text-base lg:max-w-none">
              {data.image && (
                <figure>
                  <MediaWrapper
                    media={data.image}
                    divClassName="object-center w-[50%] lg:w-full"
                    className="rounded-lg shadow-lg"
                  />
                  {data.image.caption && (
                    <figcaption className="mt-3 flex text-sm text-text-secondary">
                      <CameraIcon
                        className="h-5 w-5 flex-none text-text-secondary"
                        aria-hidden="true"
                      />
                      <span className="ml-2">{data.image.caption}</span>
                    </figcaption>
                  )}
                </figure>
              )}
            </div>
          </div>
          <div className="mt-8 lg:mt-0">
            <div className="mx-auto max-w-prose text-base lg:max-w-none">
              {data.description && (
                <MarkdownWrapper
                  text={data.description}
                  markdownClassName="text-lg text-text-secondary"
                  replaceParagraph={false}
                />
              )}
            </div>
            {data.body && data.body.text && (
              <div className="prose-prose-primary prose mx-auto mt-5 text-text-secondary lg:col-start-1 lg:row-start-1 lg:max-w-none">
                <MarkdownWrapper
                  text={data.body.text}
                  images={data.body.images}
                  imgDivClassName="w-full"
                  imgClassName="rounded-lg"
                  width={1310}
                  height={873}
                  replaceParagraph={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

TwoColumnsWithImage.propTypes = {
  data: PropTypes.object,
};

export default TwoColumnsWithImage;
